import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {DialogService, DynamicDialogRef} from "primeng/dynamicdialog";
import {MappingService} from "../../../../../core/services/mapping.service";
import {InsiderMappingService} from "../../../../../api/services/insider-mapping.service";
import {ConfirmationService, MessageService} from "primeng/api";
import {MapInsiderDataComponent} from "../../modals/map-insider-data/map-insider-data.component";
import {EditInsiderDataComponent} from "../../modals/edit-insider-data/edit-insider-data.component";
import {AsReportedInsider} from "../../../../../api/models/as-reported-insider";
import {ViewInsiderDataComponent} from "../../modals/view-insider-data/view-insider-data.component";
import {MappingFilter} from "../../../../../api/models/mapping-filter";
import {
  handlePasteWithoutLeadingSpaces,
  preventLeadingSpaceKey
} from "../../../../../core/utils/input-validation.utils";

@Component({
  selector: 'app-insider-duplicate-by-cik',
  standalone: false,
  templateUrl: './insider-duplicate-by-cik.component.html',
  styleUrl: './insider-duplicate-by-cik.component.css'
})
export class InsiderDuplicateByCikComponent implements OnChanges {
  @Input() isActive: boolean = false;
  private hasLoadedOnce = false;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["isActive"]?.currentValue === true) {
      this.hasLoadedOnce = false;
    }
  }
  ref!: DynamicDialogRef;
  formData = {
    Name: "",
    Cik: "",
  };
  insiders: any[] = [];
  selectedInsiders: any[] = [];
  selectedDialogRow: any = null;
  readonly paginator = { pageSize: 10, pageNumber: 1, totalCount: 0 };
  // Pagination for Insiders
  First = 0;
  Rows = 10;
  searchByCik?: string;
  searchByInsiderName?: string;
  cikNumber?: string;
  isLoading: boolean = false;

  constructor(
    public mappingService: MappingService,
    public dialogService: DialogService,
    public insiderService: InsiderMappingService,
    public _messageService: MessageService,
    public confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.mappingService.getInsiders().then((data) => {
      this.insiders = data;
    });
  }
  openMapModal(insider?: any, event?: Event) {
    if (event) event.preventDefault();
    this.ref = this.dialogService.open(MapInsiderDataComponent, {
      dismissableMask: false,
      closable: true,
      modal: true,
      showHeader: true,
      style: { width: "60vw" },
      contentStyle: { overflow: "auto" },
      header: "Mapping",
      data: {
        insiders: [insider],
        remap: true,
      },
    });
    this.ref.onClose.subscribe((refresh) => {
      if (refresh) {
        this.getDuplicateByCikInsiders();
      }
    });
  }
  openEditMapModal(product?: any, event?: Event) {
    if (event) event.preventDefault();
    const editData = product;
    this.ref = this.dialogService.open(EditInsiderDataComponent, {
      dismissableMask: false,
      closable: true,
      modal: true,
      showHeader: true,
      style: { width: "60vw" },
      contentStyle: { overflow: "auto" },
      data: {
        insiderData: editData,
        isEditMode: true,
      },
      header: "Edit Insider Data",
    });

    this.ref.onClose.subscribe((refresh) => {
      if (refresh) {
        this.getDuplicateByCikInsiders();
      }
    });
  }

  openViewMapModal(insider: AsReportedInsider, event?: Event) {
    if (event) event.preventDefault();
    if (!insider?.insiderId) {
      this._messageService.add({
        severity: "warn",
        summary: "Warning",
        detail: "Insider ID not found",
      });
      return;
    }
    this.isLoading = true;
    this.insiderService.getInsiderMappingDetails(insider.insiderId).subscribe({
      next: (details: AsReportedInsider[]) => {
        const detailData = details.length > 0 ? details : null;
        this.ref = this.dialogService.open(ViewInsiderDataComponent, {
          dismissableMask: false,
          closable: true,
          modal: true,
          showHeader: true,
          style: { width: "60vw" },
          contentStyle: { overflow: "auto" },
          header: "View Insider Mapping Details",
          data: {
            insiderData: detailData || [],
            noRecord: !detailData,
          },
        });

        this.ref.onClose.subscribe((result) => {
          if (result?.revertInsider) {
            this.openMapModal(result.revertInsider);
          }
        });
        this.isLoading = false;
      },
      error: (err) => {
        this._messageService.add({
          severity: "error",
          summary: "Error",
          detail:
            err.error?.message || "Failed to load insider mapping details",
        });
        this.isLoading = false;
      },
    });
  }

  onInsiderPageChange(event: any) {
    this.First = event.first;
    this.Rows = event.rows;
  }

  onUpdateClick(product: any) {
    if (product) {
      this.openEditMapModal(product);
    }
  }

  onDetailClick(product: any) {
    if (product) {
      this.openViewMapModal(product);
    }
  }

  getDuplicateByCikInsiders() {
    const params: MappingFilter = {
      searchByCikNumber: this.searchByCik,
      searchByInsiderName: this.searchByInsiderName,
      pageNumber: this.paginator.pageNumber,
      pageSize: this.paginator.pageSize,
      status: "DUPLICATE_CIK",
    };
    this.isLoading = true;
    this.insiderService.getInsiderMapping(params).subscribe({
      next: (value) => {
        this.insiders = value.DuplicateCikInsider ?? [];
      },
      error: (err) => {
        this.isLoading = false;
        this._messageService.add({
          summary: err.error.message,
          severity: "error",
        });
      },
      complete: () => {
        this.paginator.totalCount = this.insiders?.[0]?.totalCount ?? 0;
        this.isLoading = false;
      },
    });
  }

  loadData(event: any) {
    if (!this.isActive) return;
    if (!this.hasLoadedOnce && event.first === 0) {
      this.hasLoadedOnce = true;
    } else if (!this.hasLoadedOnce) {
      return;
    }

    if (event.first > 0 && this.paginator.pageSize !== event.rows) {
      this.paginator.pageNumber = 1;
      this.First = 0;
    } else {
      this.paginator.pageNumber = event.first / event.rows + 1;
      this.First = event.first;
    }

    this.paginator.pageSize = event.rows;
    this.getDuplicateByCikInsiders();
  }

  pageChange(event: any) {
    this.First = event.first;
    this.Rows = event.rows;
  }

  onSearch() {
    this.First = 0;
    this.paginator.pageNumber = 1;
    this.getDuplicateByCikInsiders();
  }

  onDeleteClick(insider: any) {
    this.confirmationService.confirm({
      message: "Are you sure you want to remove this insider?",
      header: "Confirm Remove",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        const params: InsiderMappingService.RemoveInsiderParams = {
          insiderId: insider.insiderId,
          reason: "Deleted insider successfully",
        };
        this.isLoading = true;
        this.insiderService.removeInsider(params).subscribe({
          next: (value) => {
            // handle success if needed
            this.getDuplicateByCikInsiders();
          },
          error: (err) => {
            this.isLoading = false;
            const errorMsg =
              err?.error?.message ||
              "Insider ID already mapped, you can't delete it";

            this._messageService.add({
              summary: errorMsg,
              severity: "error",
            });
          },
          complete: () => {
            this.isLoading = false;
          },
        });
      },
    });
  }
  preventSpaceKey(event: KeyboardEvent): void {
    preventLeadingSpaceKey(event);
  }

  onPaste(event: ClipboardEvent, field: "searchByCik" | "searchByInsiderName"): void {
    handlePasteWithoutLeadingSpaces(event, (newValue: string) => {
      this[field] = newValue;
    });
  }
}
